/**
 * D2-A · Provider e modale per disconnessione automatica per inattività.
 *
 * - Ascolta interazioni utente autentiche (click, keydown, touchstart)
 *   e chiama /auth/activity con throttle 60s per aggiornare last_activity_at.
 * - Legge periodicamente lo stato della sessione via /auth/keep-alive
 *   (senza toccarla) OPPURE calcola in locale il countdown se
 *   `security.idle_timeout.enabled=false`.
 * - Mostra un modale 120s prima della scadenza con "Rimani connesso" / "Esci ora".
 * - Sincronizza tra schede tramite BroadcastChannel (con fallback storage-event).
 * - Su timeout: chiama logout locale, reindirizza a /login?reason=idle.
 *
 * Nessun token/segreto memorizzato in localStorage. Salva SOLO
 * `gi_last_username` (nome utente ricordato) su login riuscito.
 */
import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api } from "@/foundation/api/client";
import { useAuth } from "@/foundation/auth/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle, LogOut } from "lucide-react";

const CTX = createContext(null);
export const useIdleSession = () => useContext(CTX);

const CHANNEL_NAME = "gi_session_v1";
const ACTIVITY_THROTTLE_MS = 60_000;  // 60s
const WARNING_LEAD_SECONDS_DEFAULT = 120;  // 2 min

// Solo per test: consente al frontend di usare secondi invece che minuti.
// In produzione idle_timeout è espresso in minuti, minimo 5.
const UNIT_SECONDS = String(process.env.REACT_APP_D2_UNIT_SECONDS || "") === "1";

function nowMs() { return Date.now(); }

export function IdleSessionProvider({ children }) {
  const { user, logout, setUser } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [config, setConfig] = useState(null);        // {enabled, minutes, quick_values, ...}
  const [deadlineMs, setDeadlineMs] = useState(null); // scadenza inattività (Date.now-based)
  const [warningOpen, setWarningOpen] = useState(false);
  const [countdown, setCountdown] = useState(WARNING_LEAD_SECONDS_DEFAULT);
  const [expired, setExpired] = useState(false);
  const lastActivityRef = useRef(nowMs());
  const throttleRef = useRef(0);
  const channelRef = useRef(null);
  const timerRef = useRef(null);

  const authed = !!(user && user.id);

  // Carica configurazione idle_timeout dopo login + deadline server-side.
  useEffect(() => {
    if (!authed) { setConfig(null); setDeadlineMs(null); setWarningOpen(false); return; }
    (async () => {
      try {
        const { data } = await api.get("/settings/security/idle-timeout");
        setConfig(data);
        if (data.enabled) {
          // Leggi lo stato sessione autoritativo dal server (deadline_iso)
          try {
            const { data: st } = await api.get("/auth/session-status");
            if (st?.session_status?.deadline_iso) {
              const dl = new Date(st.session_status.deadline_iso).getTime();
              setDeadlineMs(dl);
              // sincronizza lastActivityRef con il server
              if (st.last_activity_at) {
                lastActivityRef.current = new Date(st.last_activity_at).getTime();
              }
            }
          } catch { /* ignore */ }
        }
      } catch { /* rete o non autorizzato: ignora */ }
    })();
  }, [authed, user?.id]);

  // Configura BroadcastChannel per sincronizzazione multi-scheda.
  useEffect(() => {
    if (!authed) return;
    let ch = null;
    try {
      ch = new BroadcastChannel(CHANNEL_NAME);
      channelRef.current = ch;
      ch.onmessage = (ev) => {
        const m = ev.data || {};
        if (m.type === "activity" && typeof m.at === "number") {
          lastActivityRef.current = Math.max(lastActivityRef.current, m.at);
          if (config?.enabled) recomputeDeadline(m.at);
          setWarningOpen(false);
        } else if (m.type === "logout") {
          performLogoutLocal("logout");
        } else if (m.type === "idle_timeout") {
          performLogoutLocal("idle");
        }
      };
    } catch (_e) {
      // Fallback storage-event
      const listener = (ev) => {
        if (ev.key !== "gi_session_signal_v1" || !ev.newValue) return;
        try {
          const m = JSON.parse(ev.newValue);
          if (m.type === "activity" && typeof m.at === "number") {
            lastActivityRef.current = Math.max(lastActivityRef.current, m.at);
            if (config?.enabled) recomputeDeadline(m.at);
            setWarningOpen(false);
          } else if (m.type === "logout") performLogoutLocal("logout");
          else if (m.type === "idle_timeout") performLogoutLocal("idle");
        } catch { /* noop */ }
      };
      window.addEventListener("storage", listener);
      return () => window.removeEventListener("storage", listener);
    }
    return () => { try { ch && ch.close(); } catch { /* noop */ } };
  }, [authed, config?.enabled]);

  // D2-A · Ascolta l'evento globale emesso dall'interceptor axios quando il
  // backend risponde 401 con detail session_idle_timeout / session_logout /
  // session_admin_revoke / session_password_change.
  useEffect(() => {
    if (!authed) return;
    const onTerm = (ev) => {
      const reason = ev?.detail?.reason || "";
      broadcast({ type: reason.includes("idle") ? "idle_timeout" : "logout" });
      performLogoutLocal(reason.includes("idle") ? "idle" : "logout");
    };
    window.addEventListener("gi:session-terminated", onTerm);
    return () => window.removeEventListener("gi:session-terminated", onTerm);
  }, [authed]);  // eslint-disable-line react-hooks/exhaustive-deps

  const broadcast = useCallback((msg) => {
    try { channelRef.current && channelRef.current.postMessage(msg); } catch { /* noop */ }
    try {
      localStorage.setItem("gi_session_signal_v1", JSON.stringify({ ...msg, _t: Date.now() }));
    } catch { /* noop */ }
  }, []);

  const idleMillis = useCallback(() => {
    if (!config?.enabled) return null;
    // In modalità test i "minutes" sono trattati come secondi.
    return (config.minutes || 60) * (UNIT_SECONDS ? 1000 : 60_000);
  }, [config]);

  const recomputeDeadline = useCallback((activityAt) => {
    const m = idleMillis();
    if (!m) { setDeadlineMs(null); return; }
    const d = (activityAt || nowMs()) + m;
    setDeadlineMs(d);
    setExpired(false);
  }, [idleMillis]);

  // Registra attività "autentica" (click/keydown/touchstart).
  useEffect(() => {
    if (!authed || !config?.enabled) return;
    recomputeDeadline(lastActivityRef.current);

    const onActivity = () => {
      const now = nowMs();
      lastActivityRef.current = now;
      recomputeDeadline(now);
      setWarningOpen(false);
      // Throttle chiamata backend: max 1/min.
      if (now - throttleRef.current >= ACTIVITY_THROTTLE_MS) {
        throttleRef.current = now;
        api.post("/auth/activity").catch(() => { /* silent */ });
        broadcast({ type: "activity", at: now });
      }
    };
    const events = ["mousedown", "keydown", "touchstart"];
    events.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    return () => { events.forEach((e) => window.removeEventListener(e, onActivity)); };
  }, [authed, config?.enabled, config?.minutes, recomputeDeadline, broadcast]);

  // Tick countdown + logica preavviso.
  useEffect(() => {
    if (!authed || !config?.enabled || !deadlineMs) return;
    if (timerRef.current) clearInterval(timerRef.current);
    const lead = Math.min(
      WARNING_LEAD_SECONDS_DEFAULT,
      Math.max(1, Math.floor((idleMillis() || 0) / (UNIT_SECONDS ? 2000 : 3000))),
    );
    timerRef.current = setInterval(() => {
      const remainingMs = deadlineMs - nowMs();
      if (remainingMs <= 0) {
        // Scaduto per inattività
        setExpired(true);
        setWarningOpen(false);
        broadcast({ type: "idle_timeout" });
        performLogoutLocal("idle");
        return;
      }
      const remainingS = Math.ceil(remainingMs / 1000);
      if (remainingS <= lead && !warningOpen) {
        setWarningOpen(true);
      }
      setCountdown(remainingS);
    }, 1000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [authed, config?.enabled, deadlineMs, idleMillis, warningOpen, broadcast]);

  const performLogoutLocal = useCallback((reason) => {
    try { setUser && setUser(false); } catch { /* noop */ }
    try { localStorage.removeItem("gi_auth_session"); } catch { /* noop */ }
    // D2-A · backup del motivo per il Login (evita race con ProtectedRoute)
    try {
      sessionStorage.setItem("gi_login_reason",
        reason === "idle" ? "idle_timeout" : "logout");
    } catch { /* noop */ }
    setDeadlineMs(null); setWarningOpen(false); setExpired(true);
    // NB: NON chiamiamo /auth/logout server-side per idle: la sessione è già
    // stata revocata dal middleware backend (session_idle_timeout).
    if (loc.pathname !== "/login") {
      nav(`/login?reason=${reason === "idle" ? "idle_timeout" : "logout"}`, { replace: true });
    }
  }, [nav, loc.pathname, setUser]);

  const keepAlive = useCallback(async () => {
    try {
      const { data } = await api.post("/auth/keep-alive");
      const now = nowMs();
      lastActivityRef.current = now;
      recomputeDeadline(now);
      setWarningOpen(false);
      broadcast({ type: "activity", at: now });
      return { ok: true, data };
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) {
        performLogoutLocal("idle");
      }
      return { ok: false, error: e };
    }
  }, [recomputeDeadline, broadcast, performLogoutLocal]);

  const logoutNow = useCallback(async () => {
    try { await logout(); } catch { /* noop */ }
    broadcast({ type: "logout" });
    setWarningOpen(false); setDeadlineMs(null);
    nav("/login?reason=logout", { replace: true });
  }, [logout, nav, broadcast]);

  const value = {
    config, deadlineMs, warningOpen, countdown, expired,
    keepAlive, logoutNow,
  };

  return (
    <CTX.Provider value={value}>
      {children}
      <IdleWarningModal open={warningOpen && authed} countdown={countdown}
        onKeepAlive={keepAlive} onLogout={logoutNow} />
    </CTX.Provider>
  );
}

function IdleWarningModal({ open, countdown, onKeepAlive, onLogout }) {
  const [busy, setBusy] = useState(false);
  const handleKeep = async () => {
    setBusy(true);
    const r = await onKeepAlive();
    setBusy(false);
    if (!r?.ok) { /* toast? il fetch fallito è già gestito da auth flow */ }
  };
  return (
    <Dialog open={open} onOpenChange={() => { /* modale non chiudibile senza scelta */ }}>
      <DialogContent data-testid="idle-warning-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-700">
            <AlertTriangle className="w-5 h-5" />
            La sessione sta per scadere per inattività
          </DialogTitle>
          <DialogDescription>
            Se non intervieni verrai disconnesso automaticamente.
          </DialogDescription>
        </DialogHeader>
        <div className="py-4 text-center">
          <div className="text-4xl font-bold tabular-nums text-slate-900"
               data-testid="idle-warning-countdown">
            {Math.max(0, countdown)}s
          </div>
          <p className="text-sm text-slate-500 mt-2">al logout automatico</p>
        </div>
        <DialogFooter className="flex-col-reverse gap-2 sm:flex-row sm:justify-between">
          <Button variant="outline" onClick={onLogout} data-testid="idle-logout-btn">
            <LogOut className="w-4 h-4 mr-2" />
            Esci ora
          </Button>
          <Button onClick={handleKeep} disabled={busy} data-testid="idle-keepalive-btn">
            {busy ? "Attendere…" : "Rimani connesso"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default IdleSessionProvider;
